<script lang="ts">
import { defineComponent, ref } from 'vue';
import NavigationInnerHead from 'components/User/NavigationInnerHead.vue';
import { storeToRefs } from 'pinia';
import { OrderCancelRefundStore } from 'stores/Order/OrderCancelRefund.ts';
import ProductListItem from 'components/User/Order/ProductListItem.vue';
import OrderProductList from 'components/User/Order/OrderProductList.vue';
import _ from 'lodash';
import { cancelRefundStatusDetail, CancelRefundStatuses, RefundCancellationTypes } from 'src/core/constants/Orders';

export default defineComponent({
  name: 'CancellationAndRefund',

  components: { OrderProductList, ProductListItem, NavigationInnerHead },

  setup() {
    const orderCancelRefundStore = OrderCancelRefundStore();
    const { cancelRefundList } = storeToRefs(orderCancelRefundStore);
    const cancelRefundLoading = ref(false);
    const cancelRefundListInfo = ref({
      start: 0,
      limit: 3,
      order: 'desc',
      day: null,
    });

    function getCancelRefundList() {
      cancelRefundLoading.value = true;
      orderCancelRefundStore
        .userCancelAndRefundList(cancelRefundListInfo.value)
        .then(({ status, data }) => {
          if (status._200_) {
            pagination.value.limit = Math.floor(data.count / cancelRefundListInfo.value.limit) || 1;
          }
          cancelRefundLoading.value = false;
        })
        .catch(() => {
          cancelRefundLoading.value = false;
        });
    }

    getCancelRefundList();

    const orderProductsDialog = ref({
      model: false,
      list: null,
    });

    const pagination = ref({
      current: 1,
      limit: 1,
    });

    return {
      cancelRefundList,
      orderProductsDialog,
      pagination,
      cancelRefundListInfo,
      cancelRefundLoading,
      model: ref(null),
      options: ['Son 30 Gün', 'Son 6 Ay', '2021'],
      RefundCancellationTypes,
      getCancelRefundList,
      getRelatedProducts: (request) => {
        return RefundCancellationTypes.ORDER_REFUND === request.type ? request.cancelrefundproducts : request.orderproducts;
      },
    };
  },

  methods: {
    cancelRefundStatusDetail,
    /**
     * Sipariş id üzerinden ilgili siparişin data'sını
     * dialog içerisine aktar ve listele
     */
    orderVariationsDialog(orderId: number) {
      const product = _.find(this.cancelRefundList.list, (o) => {
        return o.id === orderId;
      });
      this.orderProductsDialog.list = this.getRelatedProducts(product);
      this.orderProductsDialog.model = true;
    },

    selectByDay(day: any) {
      const { value } = day;
      this.cancelRefundListInfo.day = value;
      this.getOrderList();
    },

    changePagination() {
      this.cancelRefundListInfo.start = (this.pagination.current - 1) * this.cancelRefundListInfo.limit;
      this.getCancelRefundList();
    },

    statusColor(status: number) {
      switch (status) {
        case CancelRefundStatuses.CANCELLATION_CONFIRMED:
          return { color: 'green', textColor: 'white' };
        case CancelRefundStatuses.CANCELLATION_CANCELLED:
          return { color: 'red', textColor: 'white' };
        default:
          return { color: 'white', textColor: 'primary' };
      }
    },
  },
});
</script>

<template>
  <q-card class="bg-transparent shadow-0">
    <div class="flex justify-between items-center q-mb-md">
      <div>
        <navigation-inner-head title="İptal & İadeler" icon="real_estate_agent" />
      </div>
      <div>
        <q-select
          dense
          label="Siparişleri Filtrele"
          standout="bg-secondary text-white"
          rounded
          transition-show="flip-up"
          transition-hide="flip-down"
          v-model="model"
          :options="options"
          style="width: 250px"
        >
          <template v-slot:after>
            <q-btn flat round dense icon="close" @click="selectByDayModel = null" v-if="selectByDayModel">
              <q-tooltip>Temizle</q-tooltip>
            </q-btn>
          </template>
        </q-select>
      </div>
    </div>
    <q-separator color="accent" />
    <div class="row">
      <div v-if="cancelRefundLoading" class="col-12 q-pa-sm">
        <div style="width: 100%" class="text-center">
          <q-linear-progress color="primary" indeterminate class="q-mb-lg" />
          <span class="text-h6 text-weight-bold">İptal/İade Talepleriniz Yükleniyor</span>
        </div>
      </div>
      <div v-else class="col-12 q-py-lg">
        <div>
          <div class="order-list-box rounded-borders overflow-hidden q-mb-xl" :key="key" v-for="(request, key) in cancelRefundList.list">
            <div class="siparis-box-ust bg-primary text-white q-pa-sm">
              <div class="row">
                <div class="col-xs-12 col-md-4">
                  <p class="q-mb-xs">
                    <span class="text-subtitle1 text-weight-bold text-grey-1">Talep Tarihi</span>
                    <br />
                    {{ $dayjs(request.date).format('DD/MM/YYYY - HH:mm') }}
                  </p>
                </div>
                <div class="col-xs-12 col-md-4">
                  <p class="text-center q-mb-xs q-mb-none">
                    <span class="text-subtitle1 text-weight-bold text-grey-1">Sipariş Kodu</span>
                    <br />
                    {{ request.orderkod }}
                  </p>
                </div>
                <div class="col-xs-12 col-md-4 flex justify-end">
                  <p class="text-center q-mb-xs">
                    <span class="text-subtitle1 text-weight-bold text-grey-1">Talep Durumu</span>
                    <br />
                    <q-chip v-bind="statusColor(request.status)">{{ cancelRefundStatusDetail(request.status).statusText }} </q-chip>
                  </p>
                </div>
              </div>
            </div>
            <div class="siparis-box-orta bg-grey-2 q-pa-sm">
              <div class="row">
                <div class="row col-xs-12 flex justify-between">
                  <div class="col-lg-7 column">
                    <p class="text-body1 q-mb-xs text-weight-bold">Açıklama:</p>
                    <p class="q-mb-xs">{{ request.note || '-' }}</p>
                  </div>
                  <div class="col-lg-5 column items-end">
                    <div class="column justify-end q-mb-sm">
                      <p class="text-body1 q-mb-xs text-weight-bold">Talep:</p>
                      <q-chip color="primary" text-color="white" square outline>
                        <span v-if="RefundCancellationTypes.ORDER_REFUND === request.type">İade İşlemi</span>
                        <span v-else-if="RefundCancellationTypes.ORDER_CANCELLATION === request.type">İptal İşlemi</span>
                      </q-chip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="siparis-box-alt bg-grey-2">
              <div class="row">
                <div class="col-xs-12 col-md-12">
                  <div class="urunler-box bg-accent text-primary text-weight-bold text-caption q-px-sm q-py-xs">
                    <span v-if="RefundCancellationTypes.ORDER_REFUND === request.type">İade Talep Edilen Ürünler</span>
                    <span v-else-if="RefundCancellationTypes.ORDER_CANCELLATION === request.type">Satın alınan ürünler</span>
                  </div>
                  <q-list>
                    <product-list-item :list="getRelatedProducts(request)" :count="1" />
                    <q-item class="bg-grey-3">
                      <q-item-section>
                        <q-item-label class="text-center">
                          <q-btn
                            unelevated
                            label=""
                            class="see-all-products-btn text-capitalize"
                            color="primary"
                            max-width="150px"
                            size=".75rem"
                            @click="orderVariationsDialog(request.id)"
                          >
                            <q-icon name="fa-solid fa-angles-right" size=".75rem" />
                            <span class="q-px-sm">Tüm Ürünleri Görüntüle</span>
                            <q-icon name="fa-solid fa-angles-left" size=".75rem" />
                          </q-btn>
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </div>
              </div>
            </div>
          </div>
          <div class="q-px-lg flex flex-center">
            <q-pagination v-model="pagination.current" :max="pagination.limit" input @update:model-value="changePagination" />
          </div>
          <order-product-list v-model="orderProductsDialog.model" :list="orderProductsDialog.list" />
        </div>
      </div>
    </div>
  </q-card>
</template>

<style scoped></style>
